import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './Overview.css';
import Icon from './Icon';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';

const Overview = () => {
  const { authenticatedFetch } = useAuth();
  const [state, setState] = useState({
    loading: true,
    error: null,
    tenants: []
  });
  const [sortConfig, setSortConfig] = useState({ column: 'percentageGood', direction: 'desc' });

  const loadOverview = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/tenant-satisfaction`);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to load overview');
      }
      const data = await response.json();
      setState({
        loading: false,
        error: null,
        tenants: data.tenants || []
      });
    } catch (error) {
      setState({
        loading: false,
        error: error.message || 'Failed to load overview',
        tenants: []
      });
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const getSortValues = (tenant) => {
    const stats = tenant?.stats || {};
    return {
      tenant: tenant?.name?.toLowerCase() || '',
      percentageGood: stats.percentageGood ?? -1,
      good: stats.good ?? 0,
      bad: stats.bad ?? 0,
      total: stats.total ?? 0
    };
  };

  const sortedTenants = useMemo(() => {
    const sorted = [...state.tenants];
    const { column, direction } = sortConfig;
    sorted.sort((a, b) => {
      const aValues = getSortValues(a);
      const bValues = getSortValues(b);
      if (aValues[column] < bValues[column]) return direction === 'asc' ? -1 : 1;
      if (aValues[column] > bValues[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [state.tenants, sortConfig]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'tenant' ? 'asc' : 'desc' };
    });
  };

  const getHealthTone = (percentage) => {
    if (percentage === null || percentage === undefined) return 'neutral';
    if (percentage >= 80) return 'great';
    if (percentage >= 60) return 'ok';
    return 'warning';
  };

  return (
    <section className="overview">
      <header className="overview-header">
        <div>
          <h1>Tenant satisfaction overview</h1>
          <p>Last 3 months · Solved & closed tickets with feedback</p>
        </div>
        <div className="overview-actions">
          <button className="refresh-button" onClick={loadOverview} disabled={state.loading}>
            <Icon name="refresh" size="small" />
            {state.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {state.error && (
        <div className="overview-alert">
          <Icon name="exclamation" size="small" />
          <span>{state.error}</span>
        </div>
      )}

      {state.loading ? (
        <div className="overview-loading">
          <div className="loading-spinner" />
          <p>Loading tenant feedback…</p>
        </div>
      ) : sortedTenants.length === 0 ? (
        <div className="tenant-empty">
          <Icon name="list" size="medium" />
          <p>No tenants available.</p>
        </div>
      ) : (
        <div className="overview-grid">
          <div className="overview-grid-row header">
            {[
              { key: 'tenant', label: 'Tenant' },
              { key: 'percentageGood', label: 'Good %' },
              { key: 'good', label: 'Good' },
              { key: 'bad', label: 'Bad' },
              { key: 'total', label: 'Responses' }
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`header-button ${sortConfig.column === key ? 'active' : ''}`}
                onClick={() => handleSort(key)}
              >
                {label}
                {sortConfig.column === key && (
                  <span className="sort-indicator">
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ))}
          </div>
          {sortedTenants.map((tenant) => {
            const stats = tenant.stats || { good: 0, bad: 0, total: 0, percentageGood: null };
            const tone = getHealthTone(stats.percentageGood);
            return (
              <div className={`overview-grid-row ${tone}`} key={tenant.id ?? tenant.name}>
                <div className="tenant-name-cell">
                  <span className="tenant-name">{tenant.name}</span>
                  {tenant.error && <span className="tenant-error">{tenant.error}</span>}
                </div>
                <div className="metric-cell">
                  {stats.percentageGood !== null ? `${stats.percentageGood}%` : '—'}
                </div>
                <div className="metric-cell good">{stats.good}</div>
                <div className="metric-cell bad">{stats.bad}</div>
                <div className="metric-cell total">{stats.total}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Overview;

